import { FC, useState, useEffect, useRef } from 'react';
import { useSubmitFormToCreateCase } from '../../../../utils/api/requests/useMutations';
import { useLocationStore } from '../../../../stores/locationStore';
import { callEvent } from '../../../../utils/Amplitude/AmplitudeUtilities';
import { StatusNotification } from '../../Notifications/StatusNotification';
import SharedModal from '../SharedModal';
import PreventNavigationModal from '../../NavigationPrompt/PreventNavigationModal';
import { IconConfetti } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  RedirectLoader,
  themeColors,
  themeSizes,
} from '../../../../styles/globalStyleVars';
import { getButtonConfig } from '../../../../utils/Buttons/buttonState';
import { MultilineTextInput } from '../../../Styled/MultilineTextInput';
import { TypographyStyled } from '../../../Styled/Typography';
import { PageLoader as CorePageLoader } from '@cfa/react-core';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useMediaQuery from '../../../../hooks/useMediaQuery';

const WebsiteFeedbackModalSubtitle: FC<{
  text: React.JSX.Element;
  alignment: 'inherit' | 'left' | 'center' | 'right' | 'justify';
}> = ({ text, alignment }) => (
  <TypographyStyled
    text={text as React.ReactNode as string}
    align={alignment}
    style={{
      color: themeColors.colorGray_8,
      marginBottom: themeSizes.md,
    }}
  />
);

interface Props {
  onClose: () => void;
  onCancel?: () => void;
  show: boolean;
  title: string;
  subTitle: React.JSX.Element;
  screenName?: string;
  inputPlaceholder?: string;
  redirectToHelpOnline?: () => void;
  userGUID?: string;
  hideTicketButton?: boolean;
  closeButtonText?: string;
}

const WebsiteFeedbackModal: FC<Props> = ({
  onClose,
  onCancel,
  show,
  title,
  subTitle,
  screenName = window.location.href,
  inputPlaceholder = 'Please describe how we can improve...',
  redirectToHelpOnline,
  userGUID,
  hideTicketButton,
  closeButtonText = 'Close',
}) => {
  const [feedbackSuccess, setFeedbackSuccess] = useState<boolean>(false);
  const [feedbackError, setFeedbackError] = useState<boolean>(false);
  const [feedbackComment, setFeedbackComment] = useState<string>('');
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const discardFromCancelRef = useRef(false);
  const breakpoints = useBreakpoints();
  const isExtraSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const { activeLocation } = useLocationStore();
  const {
    mutate: submitFeedback,
    isLoading: submitFeedbackLoading,
    error: submitFeedbackError,
    isSuccess: submitFeedbackSuccess,
    data: submitFeedbackResponseData,
  } = useSubmitFormToCreateCase('support_products_feedback', false);

  const navigate = useNavigate();
  const [amplitudeFeedbackLoading, setAmplitudeFeedbackLoading] =
    useState<boolean>(false);

  const callFeedbackEvent = () => {
    callEvent('support feedback', {
      locationNum: activeLocation,
      screenName: screenName,
      feedbackComment: feedbackComment,
    });
  };

  useEffect(() => {
    if (!amplitudeFeedbackLoading) return;

    callFeedbackEvent();
    setTimeout(() => {
      setFeedbackSuccess?.(true);
    }, 2000);
  }, [amplitudeFeedbackLoading]);

  const submitFeedbackData = {
    locationNum: activeLocation,
    variables: [
      { id: 'location_num', value: activeLocation },
      { id: 'case_contact_guid', value: userGUID ?? '' },
      { id: 'short_description', value: 'Feedback on the Support App' },
      {
        id: 'summary',
        value: feedbackComment,
      },
    ],
    formTitle: 'Support Products Feedback',
  };

  function handleSubmitFeedback() {
    !submitFeedbackLoading && feedbackSuccess && setFeedbackComment('');
    if (['Profile', 'Footer'].includes(screenName)) {
      return submitFeedback(submitFeedbackData);
    }
    if (
      [
        'Introduction',
        'Equipment Feature',
        'Search',
        'Ticket Details',
      ].includes(screenName)
    ) {
      return setAmplitudeFeedbackLoading(true);
    }
    return callFeedbackEvent();
  }

  useEffect(() => {
    if (!show) setShowDiscardModal(false);
  }, [show]);

  const clearValues = () => {
    setFeedbackError(false);
    setFeedbackSuccess(false);
    setFeedbackComment('');
  };

  const handleClose = () => {
    if (feedbackComment.length > 0 && !feedbackSuccess) {
      discardFromCancelRef.current = false;
      setShowDiscardModal(true);
    } else {
      onClose();
      clearValues();
      setAmplitudeFeedbackLoading(false);
    }
  };

  const handleCancel = () => {
    if (feedbackComment.length > 0 && !feedbackSuccess) {
      discardFromCancelRef.current = true;
      setShowDiscardModal(true);
    } else {
      onCancel?.();
      clearValues();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    if (discardFromCancelRef.current) {
      onCancel?.();
    } else {
      onClose();
      setAmplitudeFeedbackLoading(false);
    }
    clearValues();
  };

  useEffect(() => {
    if (!redirectToHelpOnline || !feedbackSuccess) return;

    setTimeout(() => {
      onClose();
      setFeedbackComment('');
      setFeedbackSuccess(false);
      redirectToHelpOnline();
    }, 2000);
  }, [redirectToHelpOnline, feedbackSuccess]);

  useEffect(() => {
    if (!show) return;
    callEvent('view feedback modal', {
      locationNum: activeLocation,
      screenName,
    });
  }, [show]);

  useEffect(() => {
    if (!submitFeedbackSuccess) return;
    setFeedbackSuccess(true);
  }, [submitFeedbackSuccess]);

  useEffect(() => {
    if (!submitFeedbackError) return;
    setFeedbackError(true);
  }, [submitFeedbackError]);

  const feedbackResponseData = submitFeedbackResponseData
    ? {
        ...submitFeedbackResponseData,
        confirmationMessages:
          submitFeedbackResponseData.confirmationMessages?.filter(
            (msg): msg is string => typeof msg === 'string'
          ) ?? [],
      }
    : undefined;

  const {
    shouldDisablePrimary,
    shouldDisableSecondary,
    primaryAction,
    secondaryAction,
    primaryText,
    secondaryText,
    isLoading,
  } = getButtonConfig({
    feedbackSuccess,
    redirectToHelpOnline,
    handleSubmitFeedback,
    setAmplitudeFeedbackLoading,
    handleClose,
    navigate,
    activeLocation,
    hideTicketButton,
    feedbackResponseData,
    feedbackComment,
    amplitudeFeedbackLoading,
    submitFeedbackLoading,
    isExtraSmallScreen,
    closeButtonText,
  });

  const modalBody = (
    <div>
      {!feedbackSuccess ? (
        <WebsiteFeedbackModalSubtitle
          text={subTitle}
          alignment={
            isExtraSmallScreen && redirectToHelpOnline ? 'center' : 'left'
          }
        />
      ) : (
        <WebsiteFeedbackModalSubtitle
          text={
            feedbackResponseData?.confirmationMessages.length ? (
              <>{feedbackResponseData?.confirmationMessages.join(' ')}</>
            ) : (
              <>Everything you shared will help us better support you.</>
            )
          }
          alignment='center'
        />
      )}
      {!feedbackSuccess ? (
        <>
          <MultilineTextInput
            rows={5}
            placeholder={inputPlaceholder}
            onChange={setFeedbackComment}
            isDisabled={submitFeedbackLoading || amplitudeFeedbackLoading}
            data-testid='feedback-comment-field'
            value={feedbackComment}
          />
        </>
      ) : (
        <>
          {redirectToHelpOnline && (
            <RedirectLoader>
              <CorePageLoader size='sm' />
              <TypographyStyled
                text='Redirecting you to Help Online.'
                variant='h4'
              />
            </RedirectLoader>
          )}
        </>
      )}
    </div>
  );

  const modalHeader = !feedbackSuccess ? (
    title
  ) : (
    <>
      <IconConfetti
        size={30}
        color={themeColors.primaryRed}
        style={{ marginBottom: themeSizes.sm }}
        id='success-icon'
      />
      <div id='success-heading'>Thank you for your help</div>
    </>
  );

  return (
    <>
      <SharedModal
        button={{
          disabled: {
            primary: shouldDisablePrimary,
            secondary: shouldDisableSecondary,
          },
          loading: { primary: isLoading },
          onClick: {
            primary: primaryAction,
            secondary: secondaryAction,
          },
          text: {
            primary: primaryText,
            secondary: secondaryText,
          },
        }}
        dataTestId='feedback-modal'
        error={{
          hasError: feedbackError,
          message: (
            <StatusNotification
              severity='error'
              boldTextContent={
                !isExtraSmallScreen
                  ? 'Failed to submit feedback, please try again.'
                  : 'Failed to submit, please try again.'
              }
              hasWrapper={true}
              anchorTop={true}
            />
          ),
        }}
        onClose={onCancel ? handleCancel : handleClose}
        show={show && !showDiscardModal}
        suppressClickOutside
        text={{
          body: modalBody,
          header: modalHeader,
        }}
        footerStyle={{ padding: `0 0 ${themeSizes.xl} 0` }}
      />
      <PreventNavigationModal
        show={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onCancel={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardConfirm}
        headerText='Discard message'
        bodyText='Leaving will discard the information you’ve added. Are you sure you want to continue?'
        confirmButtonText='Discard'
        cancelButtonText='Keep editing'
      />
    </>
  );
};

export default WebsiteFeedbackModal;
